/**
 * Offers Page
 * Extracted from App.js - Phase 7H Frontend Refactor
 * Manage offers with create, edit, delete functionality
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import ConfirmDialog from "../components/common/ConfirmDialog";
import { Tag, Plus, Target } from "lucide-react";

const OffersPage = () => {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [offers, setOffers] = useState([]);
  const [offerVisibility, setOfferVisibility] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ 
    title: "", 
    description: "", 
    discount_type: "percent",
    discount_percent: "",
    discount_amount: "",
    discount_code: "",
    is_active: true
  });
  
  // Ref to track if error toast was shown (to prevent spam)
  const loadErrorShownRef = useRef(false);

  // Fetch offers and visibility on mount
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const [offersRes, visibilityRes] = await Promise.all([
          apiFetch("/offers", { method: "GET" }),
          apiFetch("/demand/offer-visibility?days=7", { method: "GET" }).catch(() => ({ data: null }))
        ]);
        if (isMounted) {
          setOffers(offersRes.data);
          setOfferVisibility(visibilityRes.data);
          setIsLoading(false);
          loadErrorShownRef.current = false;
        }
      } catch (e) {
        if (isMounted) {
          console.error("Failed to load offers:", e);
          setIsLoading(false);
          // Only show error toast once per mount
          if (!loadErrorShownRef.current) {
            loadErrorShownRef.current = true;
            toast.error("Failed to load offers", "load_offers_error");
          }
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [apiFetch, toast]);

  // Manual refresh function (for after create/edit/delete)
  const refreshOffers = async () => {
    try {
      const res = await apiFetch("/offers", { method: "GET" });
      setOffers(res.data);
      loadErrorShownRef.current = false;
    } catch (e) {
      console.error("Failed to refresh offers:", e);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", discount_type: "percent", discount_percent: "", discount_amount: "", discount_code: "", is_active: true });
  };

  // CREATE
  const createOffer = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await apiFetch("/offers", {
        method: "POST",
        data: {
          title: formData.title,
          description: formData.description,
          discount_type: formData.discount_type,
          discount_percent: formData.discount_type === "percent" && formData.discount_percent ? parseFloat(formData.discount_percent) : null,
          discount_amount: formData.discount_type === "amount" && formData.discount_amount ? parseFloat(formData.discount_amount) : null,
          discount_code: formData.discount_code || undefined
        }
      });
      setShowCreateModal(false);
      resetForm();
      await refreshOffers();
      toast.success("Offer created successfully");
    } catch (e) {
      toast.error("Error creating offer");
    } finally {
      setIsSaving(false);
    }
  };

  // EDIT
  const openEditModal = (offer) => {
    setSelectedOffer(offer);
    setFormData({
      title: offer.title || "",
      description: offer.description || "",
      discount_type: offer.discount_type || "percent",
      discount_percent: offer.discount_percent ? String(offer.discount_percent) : "",
      discount_amount: offer.discount_amount ? String(offer.discount_amount) : "",
      discount_code: offer.discount_code || "",
      is_active: offer.is_active
    });
    setShowEditModal(true);
  };

  const updateOffer = async () => {
    if (!selectedOffer || isSaving) return;
    setIsSaving(true);
    try {
      await apiFetch(`/offers/${selectedOffer.id}`, {
        method: "PUT",
        data: {
          title: formData.title,
          description: formData.description,
          discount_type: formData.discount_type,
          discount_percent: formData.discount_type === "percent" && formData.discount_percent ? parseFloat(formData.discount_percent) : null,
          discount_amount: formData.discount_type === "amount" && formData.discount_amount ? parseFloat(formData.discount_amount) : null,
          is_active: formData.is_active
        }
      });
      setShowEditModal(false);
      setSelectedOffer(null);
      resetForm();
      await refreshOffers();
      toast.success("Offer updated successfully");
    } catch (e) {
      toast.error("Error updating offer");
    } finally {
      setIsSaving(false);
    }
  };

  // DELETE
  const openDeleteConfirm = (offer) => {
    setSelectedOffer(offer);
    setShowDeleteConfirm(true);
  };

  const deleteOffer = async () => {
    if (!selectedOffer || isSaving) return;
    setIsSaving(true);
    try {
      await apiFetch(`/offers/${selectedOffer.id}`, { method: "DELETE" });
      setShowDeleteConfirm(false);
      // Immediately remove from list (optimistic update)
      setOffers(prev => prev.filter(o => o.id !== selectedOffer.id));
      setSelectedOffer(null);
      toast.success("Offer deleted successfully");
    } catch (e) {
      toast.error("Error deleting offer");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="loading">Loading...</div>;

  // Get visibility data for each offer
  const getOfferVisibility = (offerId) => {
    if (!offerVisibility || !offerVisibility.offers) return null;
    return offerVisibility.offers.find(o => o.offer_id === offerId);
  };

  return (
    <div className="page" data-testid="offers-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Offers</h2>
          <p className="page-subtitle">Incentives and promotions shown to high-intent visitors</p>
        </div>
        <button className="btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="create-offer-btn">
          <Plus size={15} /> New Offer
        </button>
      </div>

      {offerVisibility && offerVisibility.summary && offerVisibility.summary.total_verified_high_demand_sessions > 0 && (
        <div className="offer-visibility-banner">
          <Target size={16} style={{flexShrink:0}} />
          <span className="visibility-text">
            Your offers were visible during <strong>{offerVisibility.summary.total_verified_high_demand_sessions}</strong> Verified Demand session{offerVisibility.summary.total_verified_high_demand_sessions !== 1 ? 's' : ''} in the last 7 days
          </span>
        </div>
      )}
      
      {offers.length === 0 ? (
        <div className="empty-state-card" data-testid="offers-empty-state">
          <div className="empty-state-icon"><Tag size={24} /></div>
          <h3>No offers yet</h3>
          <p>Create an offer to show targeted promotions to your high-intent visitors.</p>
          <button className="btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
            <Plus size={14} /> Create your first offer
          </button>
        </div>
      ) : (
        <div className="offers-grid">
          {offers.map(offer => {
            const visibility = getOfferVisibility(offer.id);
            return (
              <div key={offer.id} className={`offer-card ${!offer.is_active ? "inactive" : ""}`} data-testid={`offer-card-${offer.id}`}>
                <div className="offer-header">
                  <h3>{offer.title}</h3>
                  <span className={`status-badge ${offer.is_active ? "active" : "inactive"}`}>
                    {offer.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="offer-description">{offer.description}</p>
                {offer.discount_type === "percent" && offer.discount_percent && (
                  <div className="offer-discount">{offer.discount_percent}% off</div>
                )}
                {offer.discount_type === "amount" && offer.discount_amount && (
                  <div className="offer-discount">${offer.discount_amount} off</div>
                )}
                <div className="offer-stats">
                  <span>Code: <code>{offer.discount_code}</code></span>
                  <span>Redeemed: {offer.current_redemptions}/{offer.max_redemptions || "∞"}</span>
                </div>
                {/* Offer Visibility During Demand */}
                {visibility && visibility.verified_high_demand_sessions > 0 && (
                  <div className="offer-demand-visibility">
                    <span className="odv-icon">✓</span>
                    <span className="odv-text">{visibility.visibility_message}</span>
                  </div>
                )}
                <div className="offer-actions">
                  <button className="btn-secondary" onClick={() => openEditModal(offer)} data-testid={`edit-offer-${offer.id}`}>
                    Edit
                  </button>
                  <button className="btn-danger-outline" onClick={() => openDeleteConfirm(offer)} data-testid={`delete-offer-${offer.id}`}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Offer</h3>
            <div className="form-group">
              <label>Title *</label>
              <input 
                type="text" 
                value={formData.title} 
                onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                placeholder="e.g., Early Bird Special" 
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                placeholder="Describe your offer..." 
              />
            </div>
            <div className="form-group">
              <label>Discount Type</label>
              <select 
                value={formData.discount_type} 
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value, discount_percent: "", discount_amount: "" })}
              >
                <option value="percent">Percentage</option>
                <option value="amount">Dollar Amount</option>
              </select>
            </div>
            <div className="form-row">
              {formData.discount_type === "percent" ? (
                <div className="form-group">
                  <label>Discount Percent</label>
                  <input 
                    type="number" 
                    value={formData.discount_percent} 
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })} 
                    placeholder="e.g., 20" 
                    min="0"
                    max="100"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Discount Amount ($)</label>
                  <input 
                    type="number" 
                    value={formData.discount_amount} 
                    onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })} 
                    placeholder="e.g., 500" 
                    min="0"
                    step="0.01"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Discount Code (optional)</label>
                <input 
                  type="text" 
                  value={formData.discount_code} 
                  onChange={(e) => setFormData({ ...formData, discount_code: e.target.value.toUpperCase() })} 
                  placeholder="e.g., SAVE20" 
                />
                <small className="form-hint">Auto-generated if left blank</small>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={createOffer} disabled={!formData.title || isSaving}>
                {isSaving ? "Creating..." : "Create Offer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOffer && (
        <div className="modal-overlay" onClick={() => { setShowEditModal(false); setSelectedOffer(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Offer</h3>
            <div className="form-group">
              <label>Title *</label>
              <input 
                type="text" 
                value={formData.title} 
                onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                placeholder="e.g., Early Bird Special" 
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                placeholder="Describe your offer..." 
              />
            </div>
            <div className="form-group">
              <label>Discount Type</label>
              <select 
                value={formData.discount_type} 
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value, discount_percent: "", discount_amount: "" })}
              >
                <option value="percent">Percentage</option>
                <option value="amount">Dollar Amount</option>
              </select>
            </div>
            <div className="form-row">
              {formData.discount_type === "percent" ? (
                <div className="form-group">
                  <label>Discount Percent</label>
                  <input 
                    type="number" 
                    value={formData.discount_percent} 
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })} 
                    placeholder="e.g., 20" 
                    min="0"
                    max="100"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Discount Amount ($)</label>
                  <input 
                    type="number" 
                    value={formData.discount_amount} 
                    onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })} 
                    placeholder="e.g., 500" 
                    min="0"
                    step="0.01"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Discount Code</label>
                <input 
                  type="text" 
                  value={formData.discount_code} 
                  readOnly
                  className="input-readonly"
                />
                <small className="form-hint">Code cannot be changed after creation</small>
              </div>
            </div>
            <div className="form-group">
              <label className="toggle-label">
                <input 
                  type="checkbox" 
                  checked={formData.is_active} 
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} 
                />
                <span className="toggle-text">Active</span>
              </label>
              <small className="form-hint">Inactive offers won&apos;t be shown on your website</small>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => { setShowEditModal(false); setSelectedOffer(null); }}>Cancel</button>
              <button className="btn-primary" onClick={updateOffer} disabled={!formData.title || isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Offer"
        message={`Are you sure you want to delete "${selectedOffer?.title}"? This will deactivate the offer and hide it from your website.`}
        confirmLabel={isSaving ? "Deleting..." : "Delete"}
        onConfirm={deleteOffer}
        onCancel={() => { setShowDeleteConfirm(false); setSelectedOffer(null); }}
        isDestructive={true}
      />
    </div>
  );
};

export default OffersPage;
